import { Base } from "./../../framework/base";
import { IdTitle, IIdTitle } from "./../common/idTitle";
import { TaskDataRowDataType, TaskDataRowTotalType } from "./../enums";
import { IdTitleNode, IIdTitleNode } from "../common/idTitleNode";
import { ITaskDataRowCol } from "./taskDataRowCol";
import { Translations } from "../translations";

export const createUserNameField = "CreateUserName";
export const createDateField = "CreateDate";
export const modifyUserNameField = "ModifyUserName";
export const modifyDateField = "ModifyDate";

export interface ITaskTypeCol {
    field: string;
    title: string;
    dataType: TaskDataRowDataType;
    decimals: number;
    defaultStr: string;
    defaultNbr: number;
    defaultExpression: string;
    unitTitle: string;
    readOnly: boolean;
    readOnlyExpression: string;
    clearExpression: string;
    required: boolean;
    useInFilter: boolean;
    minimum: number;
    maximum: number;
    maxLength: number;
    options: string[];
    idOptions: IIdTitle[];
    idNodeOptions: IIdTitleNode[];
    objectValue: any;
    class: string;
    expression: string;
    expressionFormat: string;
    expressionIndex: number;
    defaultSortField: boolean;
    defaultSortOrderAsc: boolean;
    placeholder: string;
    lineTotalType: TaskDataRowTotalType;
    lineTotalExpression: string;
    
    selectOptionsSubSectionValuesByTitle: boolean;

    isSaveDataType(): boolean;
    isSavedTaskDataField(): boolean;
    canBeFiltered(): boolean;
}

export class TaskTypeCol implements ITaskTypeCol {
    field: string;
    title: string;
    dataType: TaskDataRowDataType;
    decimals: number;
    defaultStr: string;
    defaultNbr: number;
    defaultExpression: string;
    unitTitle: string;
    readOnly: boolean;
    readOnlyExpression: string;
    clearExpression: string;
    required: boolean;
    useInFilter: boolean;
    minimum: number;
    maximum: number;
    maxLength: number;
    options: string[];
    idOptions: IdTitle[];
    idNodeOptions: IdTitleNode[];
    objectValue: any;
    class: string;
    expression: string;
    expressionFormat: string;
    expressionIndex: number;
    defaultSortField: boolean;
    defaultSortOrderAsc: boolean;
    placeholder: string;
    lineTotalType: TaskDataRowTotalType;
    lineTotalExpression: string;

    selectOptionsSubSectionValuesByTitle: boolean;

    constructor();
    constructor(obj: ITaskTypeCol);
    constructor(obj?: any) {
        this.field = obj && obj.field || "";
        this.title = obj && obj.title || "";
        this.dataType = obj && obj.dataType || TaskDataRowDataType.String;
        this.decimals = obj && obj.decimals || 0;
        this.defaultStr = obj && obj.defaultStr || "";
        this.defaultNbr = null;
        if (!Base.isNullOrUndefined(obj) && !Base.isNullOrUndefined(obj.defaultNbr)) {
            this.defaultNbr = obj.defaultNbr;
        }
        this.defaultExpression = obj && obj.defaultExpression || "";
        this.unitTitle = obj && obj.unitTitle || "";
        this.readOnly = obj && obj.readOnly || false;
        this.readOnlyExpression = obj && obj.readOnlyExpression || "";
        this.clearExpression = obj && obj.clearExpression || "";
        this.required = obj && obj.required || false;
        this.useInFilter = obj && obj.useInFilter || false;
        this.minimum = null;
        if (!Base.isNullOrUndefined(obj) && !Base.isNullOrUndefined(obj.minimum)) {
            this.minimum = obj.minimum;
        }
        this.maximum = null;
        if (!Base.isNullOrUndefined(obj) && !Base.isNullOrUndefined(obj.maximum)) {
            this.maximum = obj.maximum;
        }
        this.maxLength = obj && obj.maxLength || null;
        this.options = [];
        if (obj && obj.options) {
            this.options = obj.options;
        }
        this.idOptions = Base.getTypedArray(obj ? obj.idOptions : null, IdTitle);
        this.idNodeOptions = Base.getTypedArray(obj ? obj.idNodeOptions : null, IdTitleNode);
        this.class = obj && obj.class || "";
        this.objectValue = obj && obj.objectValue || null;
        this.expression = obj && obj.expression || "";
        this.expressionFormat = obj && obj.expressionFormat || "";
        this.expressionIndex = obj && obj.expressionIndex || 0;
        this.defaultSortField = obj && obj.defaultSortField || false;
        this.defaultSortOrderAsc = obj && obj.defaultSortOrderAsc || false;
        this.placeholder = obj && obj.placeholder || "";
        this.lineTotalType = obj && obj.lineTotalType || TaskDataRowTotalType.None;
        this.lineTotalExpression = obj && obj.lineTotalExpression || "";
        this.selectOptionsSubSectionValuesByTitle = obj && obj.selectOptionsSubSectionValuesByTitle || false;
    }

    static sortExpressionTaskTypeCols(items: ITaskTypeCol[]) {
        if (Base.isNullOrUndefined(items) || items.length < 2) return;
        items.sort((a: ITaskTypeCol, b: ITaskTypeCol) => { return (Base.isNullOrUndefined(a) || Base.isNullOrUndefined(b)) ? 0 : (Base.numCompare(a.expressionIndex, b.expressionIndex)); });
    }

    static isSaveDataType(dataType: number): boolean {
        return dataType !== TaskDataRowDataType.Label && dataType !== TaskDataRowDataType.LineBreak;
    }

    static isSavedTaskDataField(field: string): boolean {
        const notSavedFields = [createUserNameField, createDateField, modifyUserNameField, modifyDateField];
        return notSavedFields.findIndex(i => i.equalIgnoreCase(field)) < 0;
    }

    static colCanBeUsedInFilter(taskTypeCol: ITaskTypeCol): boolean {
        if (!taskTypeCol) return false;
        return taskTypeCol.dataType === TaskDataRowDataType.Date ||
            taskTypeCol.dataType === TaskDataRowDataType.Time ||
            taskTypeCol.dataType === TaskDataRowDataType.String && taskTypeCol.idNodeOptions.length < 1 ||
            taskTypeCol.dataType === TaskDataRowDataType.TimeSpan ||
            taskTypeCol.dataType === TaskDataRowDataType.Integer ||
            taskTypeCol.dataType === TaskDataRowDataType.Number ||
            taskTypeCol.dataType === TaskDataRowDataType.Boolean;
    }

    isSaveDataType(): boolean {
        return TaskTypeCol.isSaveDataType(this.dataType);
    }

    isSavedTaskDataField(): boolean {
        return TaskTypeCol.isSavedTaskDataField(this.field);
    }

    canBeFiltered(): boolean {
        return TaskTypeCol.colCanBeUsedInFilter(this);
    };

    private static createTaskTypeCol(key: string, value: any, dataType: TaskDataRowDataType, colIndex: number): TaskTypeCol {
        const result = new TaskTypeCol();
        result.dataType = dataType;
        result.field = key;
        result.title = !Base.isNullOrUndefined(value.title) ? (value.title as string) : "";
        result.required = !Base.isNullOrUndefined(value.required) && value.required;
        result.readOnly = !Base.isNullOrUndefined(value.readOnly) && value.readOnly;
        result.readOnlyExpression = !Base.isNullOrUndefined(value.readOnlyExpression) ? (value.readOnlyExpression as string) : "";
        result.class = !Base.isNullOrUndefined(value.class) ? (value.class as string) : "";
        result.unitTitle = !Base.isNullOrUndefined(value.unitTitle) ? (value.unitTitle as string) : "";
        result.expression = !Base.isNullOrUndefined(value.expression) ? (value.expression as string) : "";
        result.expressionFormat = !Base.isNullOrUndefined(value.expressionFormat) ? (value.expressionFormat as string) : "";
        result.expressionIndex = !Base.isNullOrUndefined(value.expressionIndex) ? (value.expressionIndex as number) : colIndex;
        result.defaultSortField = !Base.isNullOrUndefined(value.defaultSortField) && value.defaultSortField;
        result.defaultSortOrderAsc = !Base.isNullOrUndefined(value.defaultSortOrderAsc) && value.defaultSortOrderAsc;
        result.placeholder = !Base.isNullOrUndefined(value.placeholder) ? (value.placeholder as string) : "";
        result.defaultExpression = !Base.isNullOrUndefined(value.defaultExpression) ? (value.defaultExpression as string) : "";
        result.lineTotalType = !Base.isNullOrUndefined(value.lineTotalType) ? (value.lineTotalType as number) : TaskDataRowTotalType.None;
        result.lineTotalExpression = !Base.isNullOrUndefined(value.lineTotalExpression) ? (value.lineTotalExpression as string) : "";
        return result;
    }

    private static getTaskTypeColFromJsonSchema(key: string, value: any, isLineSchema: boolean, colIndex: number) {
        if (!key) return null;
        if (Base.isNullOrUndefined(value)) return null;
        let result: TaskTypeCol;
        let type = !Base.isNullOrUndefined(value.type) ? (value.type as string) : "";
        type = !Base.isNullOrEmpty(type) ? type : "";
        if (type.equalIgnoreCase("date") || type.equalIgnoreCase("time")) {
            result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.Date, colIndex);
            if (type.equalIgnoreCase("time")) {
                result.dataType = TaskDataRowDataType.Time;
            }
            result.useInFilter = !Base.isNullOrUndefined(value.useInFilter) && value.useInFilter;
            result.defaultStr = !Base.isNullOrUndefined(value.default) ? (value.default as string) : "";
            return result;
        }
        if (type.equalIgnoreCase("integer") || type.equalIgnoreCase("boolean")) {
            result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.Integer, colIndex);
            if (type.equalIgnoreCase("boolean")) {
                result.dataType = TaskDataRowDataType.Boolean;
                result.defaultStr = !Base.isNullOrUndefined(value.default) ? (value.default as string) : "";
            } else {
                result.defaultNbr = !Base.isNullOrUndefined(value.default) ? (value.default as number) : null;
            }
            result.minimum = !Base.isNullOrUndefined(value.minimum) ? value.minimum as number : null;
            result.maximum = !Base.isNullOrUndefined(value.maximum) ? value.maximum as number : null;
            result.useInFilter = !Base.isNullOrUndefined(value.useInFilter) && value.useInFilter;
            return result;
        }
        if (type.equalIgnoreCase("string")) {
            result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.String, colIndex);
            result.defaultStr = !Base.isNullOrUndefined(value.default) ? (value.default as string) : "";
            result.maxLength = !Base.isNullOrUndefined(value.maxLength) ? value.maxLength as number : null;
            result.options = !Base.isNullOrUndefined(value.enum) ? value.enum as string[] : null;
            result.idOptions = !Base.isNullOrUndefined(value.idEnum) ? value.idEnum as IIdTitle[] : null;
            result.useInFilter = !Base.isNullOrUndefined(value.useInFilter) && value.useInFilter;
            result.selectOptionsSubSectionValuesByTitle = !Base.isNullOrUndefined(value.selectOptionsSubSectionValuesByTitle) && value.selectOptionsSubSectionValuesByTitle;
            return result;
        }
        if (type.equalIgnoreCase("number") || type.equalIgnoreCase("timespan")) {
            result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.Number, colIndex);
            if (type.equalIgnoreCase("timespan")) {
                result.dataType = TaskDataRowDataType.TimeSpan;
            }
            result.defaultNbr = !Base.isNullOrUndefined(value.default) ? (value.default as number) : null;
            result.minimum = !Base.isNullOrUndefined(value.minimum) ? value.minimum as number : null;
            result.maximum = !Base.isNullOrUndefined(value.maximum) ? value.maximum as number : null;
            result.decimals = !Base.isNullOrUndefined(value.decimals) ? value.decimals as number : 0;
            result.useInFilter = !Base.isNullOrUndefined(value.useInFilter) && value.useInFilter;
            return result;
        }
        if (type.equalIgnoreCase("signature")) {
            return TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.Signature, colIndex);
        }
        if (type.equalIgnoreCase("richText")) {
            return TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.RichText, colIndex);
        }
        if (!isLineSchema) {
            if (type.equalIgnoreCase("label")) {
                return TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.Label, colIndex);
            }
            if (type.equalIgnoreCase("lineBreak")) {
                return TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.LineBreak, colIndex);
            }
            if (type.equalIgnoreCase("pageBreak")) {
                return TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.PageBreak, colIndex);
            }
            if (type.equalIgnoreCase("titlePage")) {
                result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.TitlePage, colIndex);
                result.objectValue = !Base.isNullOrUndefined(value.objectValue) ? value.objectValue : null;
                return result;
            }
            if (type.equalIgnoreCase("documentReference")) {
                result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.DocumentReference, colIndex);
                result.objectValue = !Base.isNullOrUndefined(value.objectValue) ? value.objectValue : null;
                return result;
            }
            if (type.equalIgnoreCase("eventReference")) {
                result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.EventReference, colIndex);
                result.objectValue = !Base.isNullOrUndefined(value.objectValue) ? value.objectValue : null;
                return result;
            }
            if (type.equalIgnoreCase("taskReference")) {
                result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.TaskReference, colIndex);
                result.objectValue = !Base.isNullOrUndefined(value.objectValue) ? value.objectValue : null;
                return result;
            }
            if (type.equalIgnoreCase("recordReference")) {
                result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.RecordReference, colIndex);
                result.objectValue = !Base.isNullOrUndefined(value.objectValue) ? value.objectValue : null;
                return result;
            }
            return null;
        }
        if (key.equalIgnoreCase("modifyUserName")) {
            result = TaskTypeCol.createTaskTypeCol(key, value, TaskDataRowDataType.String, colIndex);
            return result;
        }
        return null;
    }

    static getJsonTaskTypeColsFromJsonSchema(schema: any, isLineSchema: boolean): TaskTypeCol[] {
        const result: TaskTypeCol[] = [];
        for (const prop in schema) {
            if (Object.prototype.hasOwnProperty.call(schema, prop)) {
                const taskTypeCol = TaskTypeCol.getTaskTypeColFromJsonSchema(prop, schema[prop], isLineSchema, result.length);
                if (taskTypeCol) {
                    result.push(taskTypeCol);
                }
            }
        }
        return result;
    }

    static isCalculatedField(item: ITaskTypeCol): boolean {
        if (!item) return false;
        return (item.dataType === TaskDataRowDataType.Number || item.dataType === TaskDataRowDataType.Integer) && !!item.expression;
    }

    static isNumberValueField(item: ITaskTypeCol): boolean {
        if (!item) return false;
        return item.dataType === TaskDataRowDataType.Integer ||
               item.dataType === TaskDataRowDataType.Number ||
               item.dataType === TaskDataRowDataType.TimeSpan ||
               item.dataType === TaskDataRowDataType.Date ||
               item.dataType === TaskDataRowDataType.Time;
    }

    static validateInputOfRequiredTaskTypeCols(typeCols: ITaskTypeCol[], rowCols: ITaskDataRowCol[], successCallback: () => void, errorCallback: (typeCol: ITaskTypeCol) => void) {
        for (let i = 0; i < typeCols.length; i++) {
            const typeCol = typeCols[i];
            if (!typeCol.required) continue;
            const rowCol = rowCols.find(j => j.field === typeCol.field);
            if (!rowCol || !rowCol.valueStr) {
                errorCallback(typeCol);
                return;
            }
        }
        if (!successCallback) return;
        successCallback();
    }
    
    static getAllInvalidRequiredTaskTypeCols(typeCols: ITaskTypeCol[], rowCols: ITaskDataRowCol[]): ITaskTypeCol[] {
        const result: ITaskTypeCol[] = [];
        for (let i = 0; i < typeCols.length; i++) {
            const typeCol = typeCols[i];
            if (!typeCol.required) continue;
            const rowCol = rowCols.find(j => j.field === typeCol.field);
            if (!rowCol || !rowCol.valueStr) {
                result.push(typeCol);
            }
        }
        return result;
    }
    
    static getTaskTypeColTitle(typeCols: ITaskTypeCol[], field: string): string {
        if (!typeCols || !field) return "";
        const typeCol = typeCols.find(i => i.field === field);
        if (!typeCol) return field;
        return typeCol.title;
    }

    private static createImplicitTaskTypeCol(field: string, title: string, dataType: TaskDataRowDataType, defaultSortField: boolean, defaultSortOrderAsc: boolean): TaskTypeCol {
        const result = new TaskTypeCol();
        result.field = field;
        result.title = title;
        result.dataType = dataType;
        result.defaultSortField = defaultSortField;
        result.defaultSortOrderAsc = defaultSortOrderAsc;
        return result;
    }

    static getImplicitJsonTaskTypeCols(): ITaskTypeCol[] {
        return [
            TaskTypeCol.createImplicitTaskTypeCol(createUserNameField, Translations.FormCreateUser, TaskDataRowDataType.String, true, true),
            TaskTypeCol.createImplicitTaskTypeCol(createDateField, Translations.FormCreateDate, TaskDataRowDataType.Date, true, false),
            TaskTypeCol.createImplicitTaskTypeCol(modifyUserNameField, Translations.FormModifyUser, TaskDataRowDataType.String, true, true),
            TaskTypeCol.createImplicitTaskTypeCol(modifyDateField, Translations.FormModifyDate, TaskDataRowDataType.Date, true, false)
        ];
    }
}

export const getSchemaObjectFromTaskTypeCol = (col: Partial<TaskTypeCol>) => ({
    type: TaskDataRowDataType[col.dataType].toLowerCase(),
    title: col.title,
    required: col.required,
    readOnly: col.readOnly,
    useInFilter: col.useInFilter,
    class: col.class,
    unitTitle: col.unitTitle,
    expression: col.expression,
    expressionFormat: col.expressionFormat,
    defaultSortField: col.defaultSortField,
    defaultSortOrderAsc: col.defaultSortOrderAsc,
    placeholder: col.placeholder,
    // readOnlyExpression
    default: col.dataType === TaskDataRowDataType.Integer || col.dataType === TaskDataRowDataType.Number
        ? col.defaultNbr
        : col.defaultStr,
    defaultExpression: col.defaultExpression,
    minimum: col.minimum,
    maximum: col.maximum,
    maxLength: col.maxLength,
    enum: col.options,
    idEnum: col.idOptions,
    // idNodeEnum
    decimals: col.decimals,
    lineTotalType: col.lineTotalType,
    lineTotalExpression: col.lineTotalExpression,
    objectValue: col.objectValue,
    // type
    // field
    // title
    // required
    // readOnly
    // useInFilter
    // class
    // unitTitle
    // expression
    // expressionFormat
    // defaultSortField
    // defaultSortOrderAsc
    // placeholder
    // readOnlyExpression
    // default
    // minimum
    // maximum
    // maxLength
    // enum
    // idEnum
    // idNodeEnum
    // decimals
    // objectValue
});

export function taskTypeColJsonSerializerReplacer(key: string, value: string | number | boolean | any[]) {
    const valueIsInArray = Array.isArray(this);
    if (
        value === null ||
        (key !== "title" && value === "") ||
        value === undefined ||
        value === false && !valueIsInArray ||
        value === 0 && !valueIsInArray
    ) {
        return undefined;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return undefined;
        }
        const filtered = value.filter(item => item !== null && item !== undefined);
        if (filtered.length === 0) {
            return undefined;
        } else {
            return filtered;
        }
    }
    return value;
};
